import type { Entry } from '@ozone/shared';

/**
 * Canonical ordering of MBR categories. MUST mirror `ENTRY_CATEGORIES` in
 * `apps/slack-bot/src/modalView.ts` — that constant drives the `/mbr create`
 * Slack dropdown and is the single source of truth (see AOZONE-36 + the
 * source-of-truth note in /CLAUDE.md). Do not add categories here from
 * memory or design docs.
 */
export const CATEGORY_ORDER = [
  'Top Customer Engagements',
  'BD Activities',
  'Market Signals',
  'Enablement Activities',
  'Asks to Accelerate Biz',
  'Other',
] as const;

export function currentMonthLabel(now: Date = new Date()): string {
  return now.toLocaleString('en-US', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

export function currentMonthKey(now: Date = new Date()): string {
  const y = now.getUTCFullYear();
  const m = String(now.getUTCMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}

export function entryHeadline(e: Entry): string {
  return (
    e.customerName ??
    e.partnerOrCloud ??
    e.target ??
    e.title ??
    e.entryCategory ??
    ''
  );
}

export function formatDealSize(e: Entry): string | null {
  if (e.dealSizeAmount == null || e.dealSizeType == null) return null;
  return `$${e.dealSizeAmount.toLocaleString('en-US')} ${e.dealSizeType}`;
}

export function filterCurrentMonth(entries: Entry[], monthKey: string): Entry[] {
  return entries.filter((e) => e.createdAt.slice(0, 7) === monthKey);
}

export interface SubmitterGroup {
  submitterAlias: string;
  submitterId: string;
  byCategory: Array<{ category: string; entries: Entry[] }>;
}

export function groupBySubmitter(entries: Entry[]): SubmitterGroup[] {
  const categorized = entries.filter((e) => e.entryCategory != null && e.entryCategory !== '');

  const bySubmitter = new Map<
    string,
    { submitterAlias: string; submitterId: string; entries: Entry[] }
  >();
  for (const e of categorized) {
    const existing = bySubmitter.get(e.submitterId);
    if (existing) {
      existing.entries.push(e);
    } else {
      bySubmitter.set(e.submitterId, {
        submitterAlias: e.submitterAlias,
        submitterId: e.submitterId,
        entries: [e],
      });
    }
  }

  const groups: SubmitterGroup[] = [];
  for (const g of bySubmitter.values()) {
    const byCategory: Array<{ category: string; entries: Entry[] }> = [];
    for (const cat of CATEGORY_ORDER) {
      const bucket = g.entries.filter((e) => e.entryCategory === cat);
      if (bucket.length > 0) byCategory.push({ category: cat, entries: bucket });
    }
    groups.push({
      submitterAlias: g.submitterAlias,
      submitterId: g.submitterId,
      byCategory,
    });
  }

  groups.sort((a, b) =>
    a.submitterAlias.toLowerCase().localeCompare(b.submitterAlias.toLowerCase()),
  );
  return groups;
}
